import { BehaviorSubject } from 'rxjs';
import { getSelectedAccountIdsForUrl } from './User';

interface AccountScopedReadOptions<T, R> {
  subject: BehaviorSubject<T>;
  request: (accountIds: string[]) => Promise<{ data: R | null; error: unknown }>;
  // What the subject holds with nothing to show: no accounts selected, or a first load that failed.
  empty: T;
  toValue: (data: R) => T;
  // Told how the newest request ended (null on success); a request a newer one overtook is never reported.
  onResult?: (error: unknown) => void;
}

export interface AccountScopedRead {
  // A first load or an account change: a failure clears, since what is held may be another account's.
  load: () => Promise<void>;
  // A background re-read after a save: a failure keeps what is already on screen.
  refresh: () => Promise<void>;
  // For a save the running read may have started too early to see.
  refreshIfRunning: () => Promise<void>;
}

// Latest request wins: a read overtaken by a newer one, as by a save landing mid-read, drops its
// answer. Overlapping reads run side by side rather than queueing.
export const accountScopedRead = <T, R>({
  subject,
  request,
  empty,
  toValue,
  onResult
}: AccountScopedReadOptions<T, R>): AccountScopedRead => {
  let latest = 0;
  let inFlight = 0;
  // Set by an account switch until a read answers, so a refresh overtaking it still clears on failure.
  let clearOnError = false;

  const read = async (keepOnError: boolean): Promise<void> => {
    if (!keepOnError) clearOnError = true;
    const seq = ++latest;
    const accountIds = getSelectedAccountIdsForUrl();
    if (!accountIds.length) {
      clearOnError = false;
      subject.next(empty);
      return;
    }

    inFlight += 1;
    const { data, error } = await request(accountIds).finally(() => (inFlight -= 1));
    if (seq !== latest) return;

    onResult?.(error ?? null);
    const clear = clearOnError;
    clearOnError = false;
    if (!error && data) subject.next(toValue(data));
    else if (clear || subject.getValue() === null) subject.next(empty);
  };

  return {
    load: () => read(false),
    refresh: () => read(true),
    refreshIfRunning: () => (inFlight > 0 ? read(true) : Promise.resolve())
  };
};
